import { Link } from 'react-router-dom';
import { ArrowRight, Check, Award } from 'lucide-react';
import Breadcrumbs from '../../components/shared/Breadcrumbs';
import AffiliateBanner from '../../components/shared/AffiliateBanner';
import CTABox from '../../components/shared/CTABox';
import TableOfContents from '../../components/shared/TableOfContents';
import StarRating from '../../components/shared/StarRating';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../../components/ui/accordion';

const BesteLLCServicesPage = () => {
  const tocItems = [
    { id: 'einfuehrung', title: 'Einführung', level: 2 },
    { id: 'kriterien', title: 'Unsere Bewertungskriterien', level: 2 },
    { id: 'vergleich', title: 'Der große Vergleich', level: 2 },
    { id: 'northwest', title: '#1 Northwest Registered Agent', level: 2 },
    { id: 'zenbusiness', title: '#2 ZenBusiness', level: 2 },
    { id: 'incfile', title: '#3 Incfile', level: 2 },
    { id: 'rai', title: '#4 Registered Agents Inc.', level: 2 },
    { id: 'legalzoom', title: '#5 LegalZoom', level: 2 },
    { id: 'fazit', title: 'Unser Fazit', level: 2 },
  ];

  const providers = [
    { 
      rank: 1,
      name: 'Northwest Registered Agent', 
      rating: 4.9, 
      price: '$39 + Staat',
      raPrice: '$125/Jahr (1. Jahr gratis)',
      speed: '1-2 Werktage',
      privacy: 'Sehr hoch',
      support: 'US-basiert, exzellent',
      verdict: 'Testsieger — Beste Gesamtleistung',
      isWinner: true 
    },
    { 
      rank: 2,
      name: 'ZenBusiness', 
      rating: 4.1, 
      price: '$0 + Staat',
      raPrice: '$199/Jahr',
      speed: '2-3 Wochen (Standard)',
      privacy: 'Mittel',
      support: 'Chat/Email',
      verdict: 'Gut für Budget-bewusste Gründer',
      isWinner: false 
    },
    { 
      rank: 3,
      name: 'Incfile', 
      rating: 3.8, 
      price: '$0 + Staat',
      raPrice: '$119/Jahr',
      speed: '2-4 Wochen (Standard)',
      privacy: 'Niedrig',
      support: 'Nur Email',
      verdict: 'Günstig, aber Einschränkungen',
      isWinner: false 
    },
    { 
      rank: 4,
      name: 'Registered Agents Inc.', 
      rating: 4.3, 
      price: '$49 + Staat',
      raPrice: '$199/Jahr',
      speed: '1-3 Werktage',
      privacy: 'Hoch',
      support: 'Telefon & Email',
      verdict: 'Solide Alternative',
      isWinner: false 
    },
    { 
      rank: 5,
      name: 'LegalZoom', 
      rating: 3.5, 
      price: '$79 + Staat',
      raPrice: '$299/Jahr',
      speed: '2-3 Wochen',
      privacy: 'Mittel',
      support: 'Telefon',
      verdict: 'Bekannte Marke, überteuert',
      isWinner: false 
    },
  ];

  const faqs = [
    {
      question: 'Was ist der beste LLC-Service für Ausländer?',
      answer: 'Northwest Registered Agent ist unsere Empfehlung für internationale Gründer. Sie haben umfangreiche Erfahrung mit nicht-US-Kunden, bieten exzellenten Datenschutz und ihr Support ist auch ohne US-Telefonnummer erreichbar.'
    },
    {
      question: 'Sind $0 LLC-Services wirklich kostenlos?',
      answer: 'Technisch ja, aber mit Einschränkungen. Die $0-Services von ZenBusiness und Incfile decken nur die Gründung ab — der Registered Agent Service kostet extra ($119-199/Jahr). Außerdem sind die Bearbeitungszeiten bei kostenlosen Services oft deutlich länger.'
    },
    {
      question: 'Warum ist LegalZoom so teuer?',
      answer: 'LegalZoom war einer der ersten Online-Gründungsservices und hat eine starke Marke aufgebaut. Sie investieren viel in Werbung und bieten ein breites Spektrum an juristischen Services. Für die reine LLC-Gründung bieten spezialisierte Anbieter jedoch mehr Wert für weniger Geld.'
    },
  ];

  return (
    <div className="py-12" data-testid="beste-llc-services-page">
      <div className="container-wide">
        <Breadcrumbs items={[
          { label: 'Ratgeber', href: '/ratgeber' },
          { label: 'Beste LLC-Gründungsservices', href: '/ratgeber/beste-llc-gruendungsservices-vergleich' }
        ]} />
        
        <AffiliateBanner />

        <div className="grid lg:grid-cols-12 gap-12">
          {/* Main Content */}
          <div className="lg:col-span-8">
            <article className="prose-reviewlex">
              <header className="mb-12">
                <h1 className="mb-6">Die 5 besten LLC-Gründungsservices 2026 im Vergleich</h1>
                <p className="text-xl text-slate-600 mb-6">
                  Wir haben über 15 LLC-Gründungsservices getestet und verglichen. Hier sind unsere 
                  Top 5 Empfehlungen für 2026 — mit detaillierten Bewertungen, Preisvergleich und 
                  unserem klaren Testsieger.
                </p>
                <div className="flex items-center gap-4 text-sm text-slate-500">
                  <span>Aktualisiert: April 2026</span>
                  <span>•</span>
                  <span>Lesezeit: 15 Min.</span>
                </div>
              </header>

              {/* Einführung */}
              <section id="einfuehrung" className="mb-16">
                <h2>Einführung</h2>
                <p>
                  Die Wahl des richtigen LLC-Gründungsservices kann überwältigend sein. Dutzende 
                  Anbieter versprechen schnelle, günstige und einfache Gründungen — aber die 
                  Unterschiede in Qualität, Datenschutz und Service sind enorm.
                </p>
                <p>
                  In diesem ausführlichen Vergleich haben wir die führenden Anbieter getestet und 
                  bewertet. Unser Fokus: Was bieten sie wirklich, was kosten sie tatsächlich 
                  (inklusive versteckter Gebühren), und wie gut ist der Service für internationale Gründer?
                </p>
              </section>

              {/* Kriterien */}
              <section id="kriterien" className="mb-16">
                <h2>Unsere Bewertungskriterien</h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="card-flat p-4">
                    <h4 className="font-heading font-bold text-navy-900 text-sm mb-2">Preis-Leistung (25%)</h4>
                    <p className="text-slate-600 text-xs">Was kostet es wirklich? Versteckte Gebühren? Was ist inklusive?</p>
                  </div>
                  <div className="card-flat p-4">
                    <h4 className="font-heading font-bold text-navy-900 text-sm mb-2">Datenschutz (25%)</h4>
                    <p className="text-slate-600 text-xs">Wird Ihre Adresse geschützt? Wer erscheint in öffentlichen Registern?</p>
                  </div>
                  <div className="card-flat p-4">
                    <h4 className="font-heading font-bold text-navy-900 text-sm mb-2">Kundenservice (25%)</h4>
                    <p className="text-slate-600 text-xs">Erreichbarkeit, Kompetenz, Wartezeiten, Support für Ausländer</p>
                  </div>
                  <div className="card-flat p-4">
                    <h4 className="font-heading font-bold text-navy-900 text-sm mb-2">Benutzerfreundlichkeit (25%)</h4>
                    <p className="text-slate-600 text-xs">Gründungsprozess, Dashboard, Dokumentenverwaltung</p>
                  </div>
                </div>
              </section>

              {/* Vergleichstabelle */}
              <section id="vergleich" className="mb-16">
                <h2>Der große Vergleich</h2>
                
                <div className="overflow-x-auto my-8">
                  <Table>
                    <TableHeader>
                      <TableRow className="table-header">
                        <TableHead>#</TableHead>
                        <TableHead>Anbieter</TableHead>
                        <TableHead>Bewertung</TableHead>
                        <TableHead>Gründungspreis</TableHead>
                        <TableHead>Registered Agent</TableHead>
                        <TableHead>Datenschutz</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {providers.map((provider) => (
                        <TableRow 
                          key={provider.rank}
                          className={provider.isWinner ? 'bg-teal-50 border-l-4 border-l-teal-600' : ''}
                        >
                          <TableCell className="font-bold">{provider.rank}</TableCell>
                          <TableCell className="font-medium">
                            <div className="flex items-center gap-2">
                              {provider.name}
                              {provider.isWinner && (
                                <span className="badge-teal text-[10px]">Testsieger</span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <StarRating rating={provider.rating} size="sm" showValue={false} />
                              <span className="text-sm">{provider.rating}</span>
                            </div>
                          </TableCell>
                          <TableCell>{provider.price}</TableCell>
                          <TableCell className="text-sm">{provider.raPrice}</TableCell>
                          <TableCell>{provider.privacy}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </section>

              {/* #1 Northwest */}
              <section id="northwest" className="mb-16">
                <h2>#1 Northwest Registered Agent — Testsieger</h2>
                
                <div className="card-highlight p-8 mb-6">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="badge-teal flex items-center gap-1">
                      <Award className="w-3 h-3" />
                      Testsieger 2026
                    </span>
                  </div>
                  <h3 className="font-heading text-xl font-bold text-navy-900 mb-3">
                    Northwest Registered Agent
                  </h3>
                  <StarRating rating={4.9} size="lg" />
                  
                  <div className="mt-6 grid sm:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-slate-500">Gründungspreis</p>
                      <p className="font-bold text-navy-900">$39 + Staatsgebühren</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Registered Agent</p>
                      <p className="font-bold text-navy-900">$125/Jahr (1. Jahr gratis)</p>
                    </div>
                  </div>
                </div>

                <p>
                  Northwest Registered Agent ist unser klarer Testsieger für 2026. Das Unternehmen 
                  kombiniert faire Preise mit exzellentem Service und — am wichtigsten — echtem 
                  Datenschutz.
                </p>
                
                <h4>Warum #1?</h4>
                <ul>
                  <li><strong>Echter Datenschutz:</strong> Northwest verwendet ihre eigene Adresse auf allen öffentlichen Dokumenten</li>
                  <li><strong>Fairer Preis:</strong> $39 + Staat mit 1 Jahr kostenlosem Registered Agent</li>
                  <li><strong>US-basierter Support:</strong> Kompetente Mitarbeiter, keine Callcenter-Skripte</li>
                  <li><strong>25+ Jahre Erfahrung:</strong> Über 3 Millionen betreute Kunden</li>
                </ul>

                <a
                  href="https://www.northwestregisteredagent.com"
                  target="_blank"
                  rel="nofollow sponsored noopener noreferrer"
                  className="cta-primary mt-6 inline-flex"
                  data-testid="northwest-cta"
                >
                  Northwest Registered Agent testen
                  <ArrowRight className="w-4 h-4 ml-2" />
                </a>
              </section>

              {/* #2 ZenBusiness */}
              <section id="zenbusiness" className="mb-16">
                <h2>#2 ZenBusiness</h2>
                
                <div className="card-flat p-6 mb-6">
                  <h3 className="font-heading text-xl font-bold text-navy-900 mb-3">ZenBusiness</h3>
                  <StarRating rating={4.1} size="md" />
                  <div className="mt-4 grid sm:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-slate-500">Gründungspreis</p>
                      <p className="font-bold text-navy-900">$0 + Staatsgebühren</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Registered Agent</p>
                      <p className="font-bold text-navy-900">$199/Jahr</p>
                    </div>
                  </div>
                </div>

                <p>
                  ZenBusiness ist eine solide Option für budget-bewusste Gründer. Die $0-Gründung 
                  klingt attraktiv, aber beachten Sie: Der Registered Agent Service kostet $199/Jahr 
                  extra, und die Standardbearbeitung dauert 2-3 Wochen.
                </p>
                
                <h4>Vorteile</h4>
                <ul>
                  <li>$0 Gründungsgebühr (nur Staatsgebühren)</li>
                  <li>Moderne, benutzerfreundliche Plattform</li>
                  <li>Gute Bewertungen für den Gründungsprozess</li>
                </ul>
                
                <h4>Nachteile</h4>
                <ul>
                  <li>Registered Agent kostet $199/Jahr extra</li>
                  <li>Ihre Adresse kann in öffentlichen Dokumenten erscheinen</li>
                  <li>Langsamere Bearbeitung bei kostenlosen Paketen</li>
                </ul>
              </section>

              {/* #3 Incfile */}
              <section id="incfile" className="mb-16">
                <h2>#3 Incfile</h2>
                
                <div className="card-flat p-6 mb-6">
                  <h3 className="font-heading text-xl font-bold text-navy-900 mb-3">Incfile</h3>
                  <StarRating rating={3.8} size="md" />
                  <div className="mt-4 grid sm:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-slate-500">Gründungspreis</p>
                      <p className="font-bold text-navy-900">$0 + Staatsgebühren</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Registered Agent</p>
                      <p className="font-bold text-navy-900">$119/Jahr</p>
                    </div>
                  </div>
                </div>

                <p>
                  Incfile bietet ebenfalls eine $0-Gründung mit dem günstigsten Registered Agent 
                  Service ($119/Jahr). Der Nachteil: minimaler Datenschutz und nur Email-Support.
                </p>
              </section>

              {/* #4 RAI */}
              <section id="rai" className="mb-16">
                <h2>#4 Registered Agents Inc.</h2>
                
                <div className="card-flat p-6 mb-6">
                  <h3 className="font-heading text-xl font-bold text-navy-900 mb-3">Registered Agents Inc.</h3>
                  <StarRating rating={4.3} size="md" />
                  <div className="mt-4 grid sm:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-slate-500">Gründungspreis</p>
                      <p className="font-bold text-navy-900">$49 + Staatsgebühren</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Registered Agent</p>
                      <p className="font-bold text-navy-900">$199/Jahr</p>
                    </div>
                  </div>
                </div>

                <p>
                  Eine solide Alternative mit gutem Datenschutz. Höhere Preise als Northwest, aber 
                  schnelle Bearbeitung und kompetenter Support.
                </p>
              </section>

              {/* #5 LegalZoom */}
              <section id="legalzoom" className="mb-16">
                <h2>#5 LegalZoom</h2>
                
                <div className="card-flat p-6 mb-6">
                  <h3 className="font-heading text-xl font-bold text-navy-900 mb-3">LegalZoom</h3>
                  <StarRating rating={3.5} size="md" />
                  <div className="mt-4 grid sm:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-slate-500">Gründungspreis</p>
                      <p className="font-bold text-navy-900">$79 + Staatsgebühren</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Registered Agent</p>
                      <p className="font-bold text-navy-900">$299/Jahr</p>
                    </div>
                  </div>
                </div>

                <p>
                  LegalZoom ist die bekannteste Marke im Markt, aber auch die teuerste. Für die 
                  reine LLC-Gründung bieten spezialisierte Anbieter mehr Wert.
                </p>
              </section>

              {/* Fazit */}
              <section id="fazit" className="mb-16">
                <h2>Unser Fazit</h2>
                
                <p>
                  <strong>Für die meisten Gründer empfehlen wir Northwest Registered Agent.</strong> 
                  Die Kombination aus fairem Preis ($39), echtem Datenschutz und exzellentem 
                  Service macht sie zur besten Wahl — besonders für internationale Gründer.
                </p>

                <div className="card-highlight p-6 mt-6">
                  <h4 className="font-heading font-bold text-navy-900 mb-3">Zusammenfassung:</h4>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-teal-600 mt-1" />
                      <span><strong>Beste Gesamtleistung:</strong> Northwest Registered Agent</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-teal-600 mt-1" />
                      <span><strong>Günstigste Option:</strong> Incfile ($0 + Staat)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-teal-600 mt-1" />
                      <span><strong>Für internationale Gründer:</strong> Northwest Registered Agent</span>
                    </li>
                  </ul>
                </div>
              </section>

              {/* FAQ */}
              <section className="mb-16">
                <h2>Häufig gestellte Fragen</h2>
                
                <Accordion type="single" collapsible className="w-full">
                  {faqs.map((faq, index) => (
                    <AccordionItem key={index} value={`faq-${index}`}>
                      <AccordionTrigger className="text-left font-heading font-semibold text-navy-900">
                        {faq.question}
                      </AccordionTrigger>
                      <AccordionContent className="text-slate-600">
                        {faq.answer}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </section>
            </article>
          </div>

          {/* Sidebar */}
          <aside className="lg:col-span-4">
            <div className="space-y-8">
              <TableOfContents items={tocItems} />
              
              <CTABox
                variant="sidebar"
                highlights={[
                  'Ab $39 + Staatsgebühren',
                  '1 Jahr Registered Agent kostenlos',
                  'Echter Datenschutz',
                  'US-basierter Support'
                ]}
              />
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default BesteLLCServicesPage;
